import { AxiosMode } from '@/axiosModel';
import axios from 'axios';

const AIPICTUREBOOK_API_BASE = `${AxiosMode.API_HOST}/api/aipicturebook`;

export type TaskStatus = 'queued' | 'running' | 'succeeded' | 'failed';
export type BookLayout =
  | 'full_image_16_9'
  | 'text_image_h_3_4'
  | 'image_text_v_3_4'
  | 'image_text_v_1_1'
  | 'cross_page_3_4'
  | 'cross_page_1_1';
export type ImageProvider = 'tencent' | 'kie' | 'huoshan';
export type ImagePhase = 'character_previews' | 'cover' | 'pages';

export interface TaskSubmitResponse {
  task_id: string;
  taskId?: string;
  id?: string | number;
  uLink?: string;
  linkName?: string;
  status: TaskStatus;
  poll_url?: string;
}

export interface TaskSnapshot<T = any> {
  task_id: string;
  task_type: string;
  status: TaskStatus;
  progress: number;
  stage: string;
  message: string;
  result: T | null;
  error?: string | null;
  created_at: number;
  updated_at: number;
  started_at?: number | null;
  finished_at?: number | null;
  metadata: Record<string, any>;
}

export interface StoryProgressSnapshot {
  /** 整体任务状态：queued / running / succeeded / failed（创建页轮询以此为准） */
  status?: TaskStatus;
  /** 综合进度 0–100 */
  progress: number;
  /** story → draft → image → transfer → done；失败时为 failed */
  stage?: string;
  message: string;
  bStatus: number;
  auditStatus: number;
  newTime: number;
  updateTime: number;
}

/** /retry 成功或进行中时返回的任务信息 */
export interface RetryStoryResult {
  task_id?: string;
  taskType?: number;
  task_status?: number;
  task_progress?: number;
  /** code === RETRY_IN_PROGRESS 时为 true */
  inProgress?: boolean;
}

export interface StoryAppearance {
  age_look?: string;
  hair?: string;
  eyes?: string;
  skin?: string;
  build?: string;
  outfit?: string;
  distinctive_feature?: string;
}

export interface StoryCharacterForm {
  id: string;
  name: string;
  description?: string;
  appearance?: StoryAppearance;
  consistency_prompt?: string;
  visual_keywords?: string[];
  reference_image_url?: string;
  generated_reference_image_url?: string;
  input_reference_image_url?: string;
}

export interface StoryCharacter {
  id: string;
  name: string;
  role?: string;
  appearance?: StoryAppearance;
  personality?: string;
  consistency_prompt?: string;
  visual_keywords?: string[];
  reference_image_url?: string;
  generated_reference_image_url?: string;
  input_reference_image_url?: string;
  forms?: StoryCharacterForm[];
  default_form_id?: string | null;
}

export interface StoryPage {
  page: number;
  narration: string;
  emotion?: string;
  tts_style?: string;
  scene_description?: string;
  scene_semantic?: Record<string, any>;
  image_url?: string;
}

export interface StoryCoverCharacter {
  id: string;
  form_id?: string | null;
  action?: string;
  position?: string;
  expression?: string;
}

export interface StoryCoverSpec {
  description?: string;
  character_ids?: string[];
  named_characters?: StoryCoverCharacter[];
}

export interface StoryBook {
  title: string;
  language: string;
  language_code?: string;
  theme: string;
  target_age?: string;
  story_type?: string;
  art_style: string;
  art_style_keywords?: string;
  style_ref_image_url?: string;
  moral?: string;
  cover_description?: string;
  cover?: StoryCoverSpec;
  characters: StoryCharacter[];
  pages: StoryPage[];
}

export interface PageCountRange {
  min: number;
  max: number;
}

export interface InputCharacterRef {
  name: string;
  setting: string;
  image_url: string;
  role?: string;
}

export interface StoryOutlineCreatePayload {
  theme: string;
  language: string;
  language_code?: string;
  art_style: string;
  story_idea?: string;
  target_age?: string;
  story_type?: string;
  perspective?: string;
  page_count?: PageCountRange;
  character_setting?: string;
  mood?: string;
  moral_goal?: string;
  extra_requirement?: string;
  style_ref_image_url?: string;
  character_refs?: InputCharacterRef[];
}

export interface StoryCreatePayload extends StoryOutlineCreatePayload {
  layout?: BookLayout;
  aspect_ratio?: string;
  page_aspect_ratio?: string;
  character_aspect_ratio?: string;
  cover_aspect_ratio?: string;
}

export interface StoryCreateResult {
  story: StoryBook;
  [key: string]: any;
}

export interface StoryOutlineCharacter extends StoryCharacter {
  consistency_prompt?: string;
  visual_keywords?: string[];
}

export interface StoryOutlinePage {
  page: number;
  narration: string;
  character_ids?: string[];
}

export interface StoryOutline {
  title: string;
  language: string;
  language_code?: string;
  theme: string;
  target_age?: string;
  story_type?: string;
  moral?: string;
  characters: StoryOutlineCharacter[];
  pages: StoryOutlinePage[];
}

export interface StoryOutlineResult {
  outline: StoryOutline;
  layout?: BookLayout;
  aspect_ratio?: string;
  generation_mode?: string;
  [key: string]: any;
}

export interface StoryOutlineRepairPayload {
  request: StoryOutlineCreatePayload;
  outline: StoryOutline;
  edit_instruction?: string;
}

export interface StoryOutlineRepairResult extends StoryOutlineResult {
  changes?: {
    added_characters?: StoryOutlineCharacter[];
    updated_characters?: StoryOutlineCharacter[];
    removed_character_ids?: string[];
    warnings?: string[];
    [key: string]: any;
  };
}

/** 对齐 /create-tts 的 voice 结构，供 /complete 一并下发配音配置 */
export interface StoryCompleteTtsVoice {
  roleId: string;
  source: string;
  pitchRate: number;
  speed: number;
  volume: number;
  /** 配音角色名称 */
  name: string;
  /** 配音角色描述 */
  description?: string;
  /** 配音角色头像 */
  portrait?: string;
  /** 配音角色分类 */
  category?: string;
}

/** 对齐 /create-tts payload；complete 时尚无工程 id 时可省略 id */
export interface StoryCompleteTtsPayload {
  voice: StoryCompleteTtsVoice;
  pages: Array<{ page: number; text: string } | null>;
  id?: string;
}

/** 对齐 /create-music payload；complete 时尚无工程 id 时可省略 id */
export interface StoryCompleteMusicPayload {
  url: string;
  name: string;
  volume: number;
  model?: string;
  id?: string;
}

/** 对齐 /images payload；complete 时由服务端生成 story，故不含 story / id */
export type StoryCompleteImagesPayload = Omit<ImageGeneratePayload, 'story' | 'id'>;

export interface StoryCompletePayload {
  request: StoryCreatePayload;
  outline: StoryOutline;
  /** 与 /create-tts 同结构的 TTS 配置 */
  tts?: StoryCompleteTtsPayload;
  /** 与 /create-music 同结构的背景音乐配置 */
  music?: StoryCompleteMusicPayload;
  /** 与 /images 同结构的生图配置（不含 story、id） */
  images?: StoryCompleteImagesPayload;
}

export interface StoryCompleteResult extends StoryOutlineResult {
  id?: string | number;
  uLink?: string;
  linkName?: string;
  story: StoryBook;
}

/** 查询故事补全费用（仅预览，不扣费、不创建任务） */
export interface StoryPreviewCostPayload {
  outline: StoryOutline;
}

export interface StoryPreviewCostResult {
  /** 预计消耗云币 */
  cost: number;
  [key: string]: any;
}

export interface StorybookFileContext {
  uLink?: string;
  linkName?: string;
}

const isAbsoluteUrl = (url: string) => /^https?:\/\//i.test(url) || url.startsWith('//');

/** 将 /images 等接口返回的相对路径转为可访问的完整图片地址。 */
export function resolveStorybookImageUrl(url: string, context?: StorybookFileContext): string {
  const normalizedUrl = url.trim();
  if (!normalizedUrl || isAbsoluteUrl(normalizedUrl)) return normalizedUrl;

  const { uLink, linkName } = context ?? {};
  if (!uLink || !linkName) return normalizedUrl;

  const filesHost = AxiosMode.FILES_HOST.replace(/\/$/, '');
  const basePath = `${filesHost}/_aipicturebook/${uLink}/${linkName}`.replace(/\/+$/, '');
  const relativePath = normalizedUrl.startsWith('/') ? normalizedUrl : `/${normalizedUrl}`;

  return `${basePath}${relativePath}`;
}

export type StoryStreamEventName =
  | 'delta'
  | 'field'
  | 'page'
  | 'narration_delta'
  | 'result'
  | 'error'
  | 'done';

export interface StoryStreamFieldEvent {
  field?: keyof StoryBook | string;
  name?: keyof StoryBook | string;
  key?: keyof StoryBook | string;
  value?: any;
}

export interface StoryNarrationDeltaEvent {
  page?: number;
  page_index?: number;
  index?: number;
  delta?: string;
  narration_delta?: string;
  text?: string;
  content?: string;
  value?: string;
}

export type StoryStreamTaskStatus =
  | 'queued'
  | 'ready'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'expired';

export interface StoryStreamTaskSnapshot {
  task_id: string;
  status: StoryStreamTaskStatus;
  queue_position?: number | null;
  poll_url?: string;
  stream_url?: string;
  message?: string;
  error?: string | null;
  result?: StoryCreateResult | null;
  [key: string]: any;
}

export interface StoryStreamHandlers {
  onDelta?: (chunk: string) => void;
  onField?: (field: StoryStreamFieldEvent | string) => void;
  onPage?: (page: StoryPage) => void;
  onNarrationDelta?: (delta: StoryNarrationDeltaEvent | string) => void;
  onTaskUpdate?: (task: StoryStreamTaskSnapshot) => void;
  onResult?: (result: StoryCreateResult) => void;
  onError?: (message: string) => void;
  signal?: AbortSignal;
}

export interface PageScriptEdit {
  page: number;
  narration?: string;
  scene_description?: string;
  edit_instruction?: string;
}

export interface StoryboardRegeneratePayload {
  story: StoryBook;
  edits: PageScriptEdit[];
  layout?: BookLayout;
  aspect_ratio?: string;
}

export interface StoryboardRegenerateResult {
  story: StoryBook;
  updated_pages?: number[];
  [key: string]: any;
}

export interface ImageGeneratePayload {
  id?: string | number;
  story: StoryBook;
  layout?: BookLayout;
  aspect_ratio?: string;
  image_provider?: ImageProvider;
  phases?: ImagePhase[];
  page_aspect_ratio?: string;
  character_aspect_ratio?: string;
  cover_aspect_ratio?: string;
  retry?: {
    max_attempts?: number;
    retry_wait_seconds?: number;
    img2img_max_attempts?: number;
    img2img_retry_wait_seconds?: number;
  };
}

export interface ImageGenerationStatsResponse {
  character_failures: Array<Record<string, string>>;
  page_failures: Array<Record<string, string>>;
  cover_failures: Array<Record<string, string>>;
  img2img_fallbacks: Array<Record<string, string>>;
  img2img_retry_failures: Array<Record<string, string>>;
  image_timings: Array<Record<string, string | number>>;
}

export interface ImageGenerateResult {
  story: StoryBook;
  cover?: {
    image_url?: string;
    prompt?: string;
    description?: string;
    character_ids?: string[];
    reference_image_urls?: string[];
    reference_mappings?: string[];
  };
  pages?: Array<{ page: number; image_url?: string; prompt?: string }>;
  stats?: ImageGenerationStatsResponse;
  [key: string]: any;
}

export interface CharacterStylizePayload {
  image_url: string;
  name?: string;
  description?: string;
  art_style: string;
  image_provider?: ImageProvider;
  aspect_ratio?: string;
}

export interface CharacterStylizeResult {
  image_url: string;
  prompt?: string;
  source_image_url?: string;
  image_provider?: ImageProvider | string;
  art_style?: string;
  aspect_ratio?: string;
  [key: string]: any;
}

export interface RegeneratePageImagePayload {
  id: number;
  page: number;
  scene_description: string;
}

export interface RegeneratePageImageResult {
  id: number;
  task_id: string;
  taskType: number;
  page: number;
  status: string;
  coinAmount: number;
}

export interface RegeneratePageImageFinalResult {
  image_url: string;
  scene_description: string;
}

export interface RegeneratePageImageOptions {
  intervalMs?: number;
  timeoutMs?: number;
  onUpdate?: (task: TaskSnapshot) => void;
}

interface ApiResponse<T> {
  code?: string;
  msg?: string;
  data?: T;
  [key: string]: any;
}

const unwrapApiData = <T = any>(response: ApiResponse<T> | T): T => {
  if (response && typeof response === 'object' && 'code' in response) {
    const apiResponse = response as ApiResponse<T>;
    if (apiResponse.code !== 'OK') {
      const error = new Error(apiResponse.msg || '请求失败') as Error & {
        code?: string;
        msg?: string;
      };
      error.code = apiResponse.code;
      error.msg = apiResponse.msg;
      throw error;
    }
    return apiResponse.data as T;
  }

  if (response && typeof response === 'object' && 'data' in response) {
    return (response as ApiResponse<T>).data as T;
  }

  return response as T;
};

const normalizeTaskSubmitResponse = (response: any): TaskSubmitResponse => {
  const data = unwrapApiData<any>(response) || {};

  return {
    ...data,
    task_id: data.task_id || data.taskId || data.id || ''
  };
};

const toStoryOutlineCreatePayload = (
  payload: StoryCreatePayload | StoryOutlineCreatePayload
): StoryOutlineCreatePayload => {
  const outlinePayload = { ...payload } as StoryCreatePayload;
  delete outlinePayload.layout;
  delete outlinePayload.aspect_ratio;
  delete outlinePayload.page_aspect_ratio;
  delete outlinePayload.character_aspect_ratio;
  delete outlinePayload.cover_aspect_ratio;

  return outlinePayload;
};

export async function createStory(payload: StoryCreatePayload): Promise<TaskSubmitResponse> {
  const res = await axios.post(`${AIPICTUREBOOK_API_BASE}/stories`, payload);
  return normalizeTaskSubmitResponse(res.data);
}

export async function createStoryOutline(
  payload: StoryOutlineCreatePayload
): Promise<TaskSubmitResponse> {
  const res = await axios.post(`${AIPICTUREBOOK_API_BASE}/story-outline`, payload);
  return normalizeTaskSubmitResponse(res.data);
}

export async function repairStoryOutline(
  payload: StoryOutlineRepairPayload
): Promise<TaskSubmitResponse> {
  const res = await axios.post(`${AIPICTUREBOOK_API_BASE}/story-outline-repair`, {
    ...payload,
    request: toStoryOutlineCreatePayload(payload.request)
  });
  return normalizeTaskSubmitResponse(res.data);
}

export async function completeStoryFromOutline(
  payload: StoryCompletePayload
): Promise<TaskSubmitResponse> {
  const res = await axios.post(`${AIPICTUREBOOK_API_BASE}/story-complete`, payload);
  return normalizeTaskSubmitResponse(res.data);
}

/**
 * 查询故事补全预计云币费用。
 * 应在 story-outline 之后、story-complete 之前调用；仅预览，不扣费。
 */
export async function previewStoryCost(
  payload: StoryPreviewCostPayload
): Promise<StoryPreviewCostResult> {
  const res = await axios.post(`${AIPICTUREBOOK_API_BASE}/preview-cost`, payload);
  const data = unwrapApiData<any>(res.data) ?? {};
  const raw =
    typeof data === 'number'
      ? data
      : (data.cost ?? data.total ?? data.total_cost ?? data.price ?? data.amount ?? 0);
  const cost = Number(raw);
  return {
    ...(typeof data === 'object' && data ? data : {}),
    cost: Number.isFinite(cost) ? cost : 0
  };
}

export async function regenerateStoryboard(
  payload: StoryboardRegeneratePayload
): Promise<TaskSubmitResponse> {
  const res = await axios.post(`${AIPICTUREBOOK_API_BASE}/regenerate`, payload);
  return normalizeTaskSubmitResponse(res.data);
}

export async function generateImages(payload: ImageGeneratePayload): Promise<TaskSubmitResponse> {
  const res = await axios.post(`${AIPICTUREBOOK_API_BASE}/images`, payload);
  return normalizeTaskSubmitResponse(res.data);
}

export async function regeneratePageImage(
  payload: RegeneratePageImagePayload,
  options: RegeneratePageImageOptions = {}
): Promise<RegeneratePageImageFinalResult> {
  // 1. 提交生成任务
  const res = await axios.post(`${AIPICTUREBOOK_API_BASE}/regenerate-page-image`, payload);
  const { task_id } = unwrapApiData<RegeneratePageImageResult>(res.data);

  if (!task_id) {
    throw new Error('任务提交失败：未返回 task_id');
  }

  // 2. 轮询等待任务完成
  const task = await pollTask<{ page: StoryPage }>(task_id, {
    intervalMs: options.intervalMs ?? 3000,
    timeoutMs: options.timeoutMs ?? 900000,
    onUpdate: options.onUpdate
  });

  if (task.status === 'failed') {
    throw new Error(task.error || '图片生成失败');
  }

  // 3. 提取结果并修复协议相对 URL（以 // 开头 → 补上 https:）
  const rawImageUrl = task.result?.page?.image_url ?? '';
  const imageUrl = rawImageUrl.startsWith('//') ? `https:${rawImageUrl}` : rawImageUrl;
  const sceneDescription = task.result?.page?.scene_description ?? payload.scene_description;

  return { image_url: imageUrl, scene_description: sceneDescription };
}

export async function stylizeCharacter(
  payload: CharacterStylizePayload
): Promise<TaskSubmitResponse> {
  const res = await axios.post(`${AIPICTUREBOOK_API_BASE}/characters/stylize`, payload);
  return normalizeTaskSubmitResponse(res.data);
}

export async function getTask<T = any>(taskId: string): Promise<TaskSnapshot<T>> {
  if (!taskId) throw new Error('任务 ID 不能为空');

  const res = await axios.post(`${AIPICTUREBOOK_API_BASE}/task-progress`, { task_id: taskId });
  return unwrapApiData<TaskSnapshot<T>>(res.data);
}

export async function getStoryProgress(storyId: string | number): Promise<StoryProgressSnapshot> {
  if (storyId === undefined || storyId === null || storyId === '') {
    throw new Error('故事 ID 不能为空');
  }

  const res = await axios.post(`${AIPICTUREBOOK_API_BASE}/get-progress`, { id: storyId });
  return unwrapApiData<StoryProgressSnapshot>(res.data);
}

export async function retryStory(storyId: string | number): Promise<RetryStoryResult> {
  if (storyId === undefined || storyId === null || storyId === '') {
    throw new Error('故事 ID 不能为空');
  }

  const res = await axios.post(`${AIPICTUREBOOK_API_BASE}/retry`, { id: storyId });
  const response = res.data as ApiResponse<RetryStoryResult> | RetryStoryResult;

  if (response && typeof response === 'object' && 'code' in response) {
    const apiResponse = response as ApiResponse<RetryStoryResult>;
    // 进行中：视为可继续轮询，不抛错
    if (apiResponse.code === 'RETRY_IN_PROGRESS') {
      return {
        ...(apiResponse.data ?? {}),
        inProgress: true
      };
    }
    if (apiResponse.code !== 'OK') {
      const error = new Error(apiResponse.msg || '重试失败') as Error & {
        code?: string;
        msg?: string;
      };
      error.code = apiResponse.code;
      error.msg = apiResponse.msg;
      throw error;
    }
    return apiResponse.data ?? {};
  }

  return (response as RetryStoryResult) ?? {};
}

/**
 * 按作品工程 ID 轮询 /get-progress，直到 succeeded / failed。
 * 单次查询的任意错误（网络、5xx、业务码非 OK 等）一律忽略并继续轮询。
 */
export async function pollStoryProgress(
  storyId: string | number,
  options: {
    intervalMs?: number;
    timeoutMs?: number;
    onUpdate?: (snapshot: StoryProgressSnapshot) => void;
  } = {}
): Promise<StoryProgressSnapshot> {
  if (storyId === undefined || storyId === null || storyId === '') {
    throw new Error('故事 ID 不能为空');
  }

  const intervalMs = options.intervalMs ?? 3000;
  const timeoutMs = options.timeoutMs ?? 900000;
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    try {
      // validateStatus：HTTP 4xx/5xx 不走 axios 抛错，统一在下方忽略
      const res = await axios.post(
        `${AIPICTUREBOOK_API_BASE}/get-progress`,
        { id: storyId },
        { validateStatus: () => true }
      );

      if (res.status < 200 || res.status >= 300) {
        console.warn('[get-progress] HTTP 错误，忽略并重试:', res.status);
      } else {
        const body = res.data as ApiResponse<StoryProgressSnapshot> | StoryProgressSnapshot;
        const businessCode =
          body && typeof body === 'object' && 'code' in body
            ? (body as ApiResponse<StoryProgressSnapshot>).code
            : undefined;

        if (businessCode && businessCode !== 'OK') {
          // 业务失败（含服务器繁忙等）忽略，不中断轮询
          console.warn('[get-progress] 业务错误，忽略并重试:', businessCode);
        } else {
          const snapshot = unwrapApiData<StoryProgressSnapshot>(body);
          options.onUpdate?.(snapshot);

          if (snapshot.status === 'succeeded' || snapshot.status === 'failed') {
            return snapshot;
          }

          // 兼容旧字段：无 status 时回退 bStatus
          if (!snapshot.status) {
            if (snapshot.bStatus === 1) return { ...snapshot, status: 'succeeded' };
            if ([-8, -9, -11].includes(snapshot.bStatus)) {
              return { ...snapshot, status: 'failed' };
            }
          }
        }
      }
    } catch (error) {
      // 网络断开、解析异常等一律忽略，继续轮询
      console.warn('[get-progress] 查询失败，忽略并重试:', error);
    }

    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }

  throw new Error('任务轮询超时');
}

export async function pollTask<T = any>(
  taskId: string,
  options: {
    intervalMs?: number;
    timeoutMs?: number;
    onUpdate?: (task: TaskSnapshot<T>) => void;
  } = {}
): Promise<TaskSnapshot<T>> {
  const intervalMs = options.intervalMs ?? 3000;
  const timeoutMs = options.timeoutMs ?? 180000;
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    try {
      const task = await getTask<T>(taskId);
      options.onUpdate?.(task);

      if (task.status === 'succeeded' || task.status === 'failed') return task;
    } catch (error) {
      // 偶发网关超时等瞬时错误（如 504）忽略，继续轮询
      console.warn('[task-progress] 查询失败，将重试:', error);
    }

    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }

  throw new Error('任务轮询超时');
}
